import { useDrinkHistory } from '@/features/drink-history/api/use-drink-history'
import { useNavigate } from '@tanstack/react-router'
import { Coffee, Plus, ChevronRight } from 'lucide-react'
import { Button, Skeleton } from '@/shared/ui'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

/**
 * Превью дневника на странице профиля
 * 
 * Компактный список последних записей с быстрым добавлением.
 */
export function ProfileDiaryPreview() {
  const { data, isLoading } = useDrinkHistory({ limit: 4 })
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  const entries = data?.data || []
  const hasEntries = entries.length > 0

  const handleViewAll = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary' })
  }

  const handleAddEntry = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary/add' })
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 delay-100">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-ink dark:text-white">
          Чайный дневник
        </h2>
        {hasEntries && (
          <button
            onClick={handleViewAll}
            className="flex items-center gap-0.5 text-xs text-primary dark:text-primary-light font-medium"
          >
            Все
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {hasEntries ? (
        <div className="space-y-2">
          {entries.map((entry) => (
            <DiaryEntryRow key={entry.id} entry={entry} onClick={handleViewAll} />
          ))}
          
          {/* Кнопка добавить */}
          <button
            onClick={handleAddEntry}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-gray-200 dark:border-dark-border text-primary dark:text-primary-light text-sm font-medium transition-colors hover:bg-primary/5 dark:hover:bg-primary/10 active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" />
            Добавить
          </button>
        </div>
      ) : (
        <EmptyState onAdd={handleAddEntry} />
      )}
    </div>
  )
}

function DiaryEntryRow({ 
  entry, 
  onClick 
}: { 
  entry: { id: string; teaName?: string; drankAt: string; rating?: number }
  onClick: () => void 
}) {
  const date = new Date(entry.drankAt)
  const timeAgo = getTimeAgo(date)

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-elevated transition-colors hover:bg-gray-100 dark:hover:bg-dark-card active:scale-[0.98]"
    >
      <div className="w-9 h-9 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
        <Coffee className="w-4 h-4 text-primary dark:text-primary-light" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-ink dark:text-white truncate">
          {entry.teaName || 'Чай'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {timeAgo}
        </p>
      </div>
      {entry.rating && (
        <div className="flex items-center gap-0.5 text-amber-500">
          <span className="text-xs font-medium">{entry.rating}</span>
          <span className="text-xs">★</span>
        </div>
      )}
    </button>
  )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="text-center py-6">
      <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-dark-elevated flex items-center justify-center mx-auto mb-3">
        <Coffee className="w-6 h-6 text-gray-400" />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
        Начните вести дневник
      </p>
      <Button
        variant="primary"
        size="sm"
        leftIcon={<Plus className="w-4 h-4" />}
        onClick={onAdd}
      >
        Добавить запись
      </Button>
    </div>
  )
}

function getTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Только что'
  if (diffMins < 60) return `${diffMins} мин назад`
  if (diffHours < 24) return `${diffHours} ч назад`
  if (diffDays === 1) return 'Вчера'
  if (diffDays < 7) return `${diffDays} дн назад`
  
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}
